import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  api,
  apiHeaders,
  setTableTicket,
  commitOrder,
  redeemReward as redeemRewardRequest,
  syncApiUrl,
  syncConnector,
  type CommitOrderItemInput,
  getAuthToken,
} from "@cliqsuite/db/powersync";
import { accountToken, loadBag, saveBag, saveTable } from "./account";
import type { KitchenTicket, Member, Table, Reward, Voucher, Order, TableSession, DeliveryAddress, OnlineSettings } from "@cliqsuite/db/schemas";
import { deliveryAddressSchema, onlineSettingsSchema, tableSchema } from "@cliqsuite/db/schemas";
import { db, useRows, type GuestStore } from "./db";
import { newId, nowIso } from "./ids";
import { useInstalledModules } from "./useInstalledModules";
import { useLoyaltyEarnRate } from "./useLoyaltyEarnRate";

/** One chosen option from the item customizer — persisted as
 * sales.order_item_option_selections on checkout. Same shape as POS's own
 * CartLineSelection (apps/pos/src/types.ts) — guest portal doesn't share a
 * package with POS any more than it does with Kitchen Display, and this is
 * small enough not to warrant one. */
export interface CartLineSelection {
  optionId: string | null;
  groupLabel: string;
  label: string;
  priceDelta: number;
}

/** A line in the guest's in-progress cart. Not a synced record. `key`
 * distinguishes two differently-customized lines of the SAME product (e.g.
 * one "Large + oat milk", one "Small + no sugar") — a plain, uncustomized
 * add keys on productId alone, same as POS's own addProduct/
 * addConfiguredProduct split (PosApp.tsx). */
export interface CartLine {
  key: string;
  productId: string;
  name: string;
  icon: string;
  imageUrl: string | null;
  color: string;
  unitPrice: number;
  qty: number;
  variantLabel?: string | null;
  /** A product sold in choices (size, colour): which catalog.product_variants row this line is. */
  variantId?: string | null;
  selections?: CartLineSelection[];
}

/** How a shop order (a store with no tables or kitchen) is placed: its own order every time, no kitchen ticket. */
export interface PlaceOptions {
  shop?: boolean;
  delivery?: DeliveryAddress | null;
  deliveryFee?: number;
  paymentMethod?: string;
  /** Paid by transfer: the uploaded screenshot's key. The order then waits for the store to check it. */
  proofKey?: string;
  /** The offer on the bag and what it takes off. The server works the amount out again from the offer. */
  promo?: { id: string; discount: number } | null;
  /** A voucher used instead of an offer. The server checks it, works the amount out again and marks it used. */
  voucher?: { id: string; discount: number } | null;
}

const orderKey = (storeId: string) => `cliqsuite_guest_order:${storeId}`;
function readOrderId(storeId: string): string | null {
  try {
    return localStorage.getItem(orderKey(storeId));
  } catch {
    return null;
  }
}
/** Stops following the store's order on this phone (it is done, and the guest is off to its receipt). */
export function forgetOrderId(storeId: string) {
  try {
    localStorage.removeItem(orderKey(storeId));
  } catch {
    // nothing stored
  }
}

// The table this phone scanned into, kept so a reload stays at the table (its order, a call for
// staff). Four hours after the page was last opened it is dropped and the guest scans again:
// the location check only happens at the scan.
const TABLE_TTL_MS = 4 * 60 * 60 * 1000;
const tableKey = (storeId: string) => `cliqsuite_guest_table:${storeId}`;
/** The table this phone scanned, with the ticket that proves it. A table kept from before tickets, or whose ticket ran out, is let go: the guest scans again. */
function readTable(storeId: string): { table: Table; ticket: string } | null {
  try {
    const held = JSON.parse(localStorage.getItem(tableKey(storeId)) ?? "null") as { table?: unknown; ticket?: string; at?: number } | null;
    if (!held?.ticket || Date.now() - (held.at ?? 0) > TABLE_TTL_MS) return null;
    if ((JSON.parse(atob(held.ticket.split(".")[1] ?? "")) as { exp: number }).exp * 1000 < Date.now()) return null;
    const parsed = tableSchema.safeParse(held.table);
    return parsed.success ? { table: parsed.data, ticket: held.ticket } : null;
  } catch {
    return null;
  }
}
function writeTable(storeId: string, table: Table | null, ticket: string | null) {
  try {
    if (table && ticket) localStorage.setItem(tableKey(storeId), JSON.stringify({ table, ticket, at: Date.now() }));
    else localStorage.removeItem(tableKey(storeId));
  } catch {
    // private mode: the table is kept for this visit only
  }
}

const ADDRESS_KEY = "cliqsuite_guest_address";
function readAddress(): DeliveryAddress | null {
  try {
    const parsed = deliveryAddressSchema.safeParse(JSON.parse(localStorage.getItem(ADDRESS_KEY) ?? "null"));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

interface GuestSessionValue {
  store: GuestStore;
  /** crm module gate — loyalty/rewards/vouchers/offers only exist when this is true. */
  hasCrm: boolean;
  table: Table | null;
  scanned: boolean;
  /** Still finding out whether the account is at a table here (asked the server, or placing it). */
  joining: boolean;
  markScanned: (table: Table | null, ticket?: string | null) => void;

  cart: CartLine[];
  cartCount: number;
  cartTotal: number;
  addToCart: (line: Omit<CartLine, "qty" | "key">) => void;
  /** For a product with variant groups (product.has_variants) — MenuPage
   * opens CustomizerSheet instead of adding straight to cart; its confirm
   * callback lands here. */
  addConfiguredItem: (line: Omit<CartLine, "qty" | "key">, qty: number, selections: CartLineSelection[]) => void;
  /** One choice of a product sold in choices; `line.variantId` says which. */
  addVariantItem: (line: Omit<CartLine, "qty" | "key">, qty: number) => void;
  /** This guest orders from the online store (delivery or pick-up, payment review): the store is a shop and they are not at a scanned table. */
  isShop: boolean;
  /** The store takes orders from a scanned table QR. */
  tableOrders: boolean;
  online: OnlineSettings;
  fulfilment: "delivery" | "pickup";
  setFulfilment: (f: "delivery" | "pickup") => void;
  /** Kept on this phone for the next order. */
  address: DeliveryAddress | null;
  saveAddress: (a: DeliveryAddress, remember: boolean) => void;
  incQty: (key: string) => void;
  decQty: (key: string) => void;
  /** Summed across every cart line for this product, regardless of which
   * variant/selection each line has — matches POS's own cartQtyByProduct,
   * used for a product tile's "already in cart" badge. */
  qtyOf: (productId: string) => number;

  specialReqs: string;
  setSpecialReqs: (v: string) => void;

  placedOrderId: string | null;
  placing: boolean;
  placeError: string | null;
  /** True once the order reached the server. */
  placeOrder: (opts?: PlaceOptions) => Promise<boolean>;

  memberId: string | null;
  member: Member | null;
  /** This phone holds a card for the business, but the member has not synced in yet. */
  memberPending: boolean;
  /** memberId + the server's member ticket from POST /auth/guest-member. */
  signInAs: (memberId: string, ticket: string) => void;
  signOut: () => void;

  // Loyalty rewards catalog (crm.rewards) and redeemed vouchers (crm.vouchers) —
  // real, store-scoped @cliqsuite/db collections.
  rewards: Reward[];
  vouchers: Voucher[];
  redeemReward: Reward | null;
  askRedeem: (rewardId: string) => void;
  cancelRedeem: () => void;
  /** Resolves to the new voucher's id, or null when the redeem did not go through. */
  confirmRedeem: () => Promise<string | null>;
  redeemError: string | null;
  memberStoreBalance: number;
  memberLifetimePoints: number;

  // Request Bill — backed by floor.table_sessions (status/bill_requested_at)
  // instead of a marker written into orders.notes.
  billRequested: boolean;
  requestBill: () => Promise<void>;

  // Call Staff — same floor.table_sessions row, its own independent
  // staff_called_at flag (not a `status` value, since it can happen
  // regardless of bill state, and unlike Request Bill doesn't require an
  // order to already be placed).
  staffCalled: boolean;
  callStaff: () => Promise<void>;
}

const GuestSessionContext = createContext<GuestSessionValue | null>(null);

// Per business: a member ticket only opens that business's loyalty data.
const memberKey = (businessId: string) => `cliqsuite_guest_member:${businessId}`;

/** One row per kitchen "round" — see packages/db's kitchenTicketSchema doc. Mirrors PosApp.tsx's own newKitchenTicket. */
function newKitchenTicket(orderId: string, storeId: string, now: string): KitchenTicket {
  return {
    id: newId(),
    order_id: orderId,
    store_id: storeId,
    status: "new",
    done_at: null, nudged_at: null, started_at: null,
    created_at: now,
    updated_at: now,
  };
}

// Local writes; PowerSync's upload queue sends them to /floor/table-sessions (retrying while offline).
async function insertSession(row: Partial<TableSession>): Promise<string> {
  const id = newId();
  const now = nowIso();
  const full: Record<string, unknown> = { ...row, created_at: now, updated_at: now };
  const cols = Object.keys(full);
  await db.execute(
    `INSERT INTO table_sessions (id, ${cols.join(", ")}) VALUES (?, ${cols.map(() => "?").join(", ")})`,
    [id, ...cols.map((c) => full[c] ?? null)],
  );
  return id;
}

async function updateSession(id: string, changes: Partial<TableSession>): Promise<void> {
  const cols = Object.keys(changes) as (keyof TableSession)[];
  await db.execute(`UPDATE table_sessions SET ${cols.map((c) => `${c} = ?`).join(", ")} WHERE id = ?`, [
    ...cols.map((c) => changes[c] ?? null),
    id,
  ]);
}

export function GuestSessionProvider({ store, children }: { store: GuestStore; children: ReactNode }) {
  const { modules: installedModules, loading: modulesLoading } = useInstalledModules(store.business_id);
  const hasCrm = installedModules.has("crm");
  const online = useMemo(() => onlineSettingsSchema.parse(store.online ?? {}), [store.online]);
  // Modules are per business, so the owner says per store whether it sells as a shop (a goods store beside the café).
  const shopOn = online.shop ?? !installedModules.has("restaurant");
  // Table QR ordering is its own switch: a store can take delivery orders and still serve tables.
  const tableOrders = online.table_orders ?? installedModules.has("restaurant");
  const [fulfilmentChoice, setFulfilment] = useState<"delivery" | "pickup">("delivery");
  const fulfilment = !online.delivery ? "pickup" : !online.pickup ? "delivery" : fulfilmentChoice;
  const [address, setAddress] = useState<DeliveryAddress | null>(readAddress);
  const saveAddress = useCallback((a: DeliveryAddress, remember: boolean) => {
    setAddress(a);
    try {
      if (remember) localStorage.setItem(ADDRESS_KEY, JSON.stringify(a));
      else localStorage.removeItem(ADDRESS_KEY);
    } catch {
      // private mode: the address just isn't remembered
    }
  }, []);
  const earnRate = useLoyaltyEarnRate(store.id);
  // The scanned table is held as scanned; whether it counts waits for the business's modules, which
  // can land a moment after the scan on a first visit.
  const [heldTable, setTable] = useState<Table | null>(() => readTable(store.id)?.table ?? null);
  // The proof of the scan. Set for every request before any effect below runs.
  const [tableTicket, setTicket] = useState<string | null>(() => readTable(store.id)?.ticket ?? null);
  setTableTicket(heldTable ? tableTicket : null);
  const table = tableOrders ? heldTable : null;
  const [scanned, setScanned] = useState(heldTable !== null);
  // A guest at a scanned table orders to the kitchen; everyone else gets the shop when it is on.
  const isShop = shopOn && !(tableOrders && table);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [specialReqs, setSpecialReqs] = useState("");

  // Memoizes the whole placeOrder attempt (order id, kitchen ticket, item/
  // selection rows) so a retry after a dropped connection (see sync-api's
  // /sales/orders/commit doc) reuses the exact same ids instead of minting
  // fresh ones — sync-api's dedup check is keyed on these ids matching a
  // prior attempt. Deliberately separate from `placedOrderId`, which means
  // something different (a confirmed prior order this session can append
  // to) — this ref only ever holds an in-flight or just-failed attempt, and
  // is cleared whenever the cart changes (a genuinely new attempt), not on
  // every render.
  const placeAttemptRef = useRef<{
    isNewOrder: boolean;
    orderId: string;
    newTicket: KitchenTicket | null;
    items: CommitOrderItemInput[];
  } | null>(null);
  useEffect(() => {
    placeAttemptRef.current = null;
  }, [cart]);
  // An order with no table is remembered on the phone, so reloading the page, or the app updating
  // itself, keeps following it. At a table the order comes from the table instead (adoptTable).
  const [placedOrderId, setPlacedOrderId] = useState<string | null>(() => (heldTable ? null : readOrderId(store.id)));
  const { data: followed } = useRows("orders", "SELECT * FROM orders WHERE id = ?", [placedOrderId ?? ""]);
  /** The order this phone follows has been paid for and closed, or cancelled. */
  const orderOver = !!followed[0] && followed[0].status !== "open";
  // A kitchen order adds new items to the order it follows, so one that was already over when the
  // page loaded (kept from an earlier visit) is let go. One that ends while the guest watches stays.
  const seenOpen = useRef<string | null>(null);
  useEffect(() => {
    const row = followed[0];
    if (!row) return;
    if (row.status === "open") seenOpen.current = row.id;
    else if (seenOpen.current !== row.id && !isShop) setPlacedOrderId(null);
  }, [followed, isShop]);
  const [placing, setPlacing] = useState(false);
  const [placeError, setPlaceError] = useState<string | null>(null);
  const [redeemReward, setRedeemReward] = useState<Reward | null>(null);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [tableSessionId, setTableSessionId] = useState<string | null>(null);

  const [signedIn, setSignedIn] = useState<{ memberId: string; ticket: string } | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(memberKey(store.business_id)) ?? "null");
    } catch {
      return null;
    }
  });
  const memberId = signedIn?.memberId ?? null;
  // A signed-in guest's open online order comes from the server, by their account, so a phone and a
  // desktop on the same account follow the same order. The id kept on this device only covers a guest
  // who has not signed in.
  const [accountTableId, setAccountTableId] = useState<string | null>(null);
  const accountTicket = useRef<string | null>(null);
  const [asking, setAsking] = useState(() => !!(signedIn || accountToken()));
  useEffect(() => {
    const token = accountToken();
    if (!signedIn && !token) return setAsking(false);
    if (!shopOn && !tableOrders) return;
    let cancelled = false;
    // Asked when the store opens and whenever the page comes back into view, so a table scanned on
    // another device meanwhile is picked up without a reload.
    const ask = () =>
      void api<{ orderId: string | null; tableId: string | null; tableTicket?: string | null }>("/auth/guest-open-order", { ticket: signedIn?.ticket, accountToken: token, storeId: store.id })
        .then((r) => {
          if (cancelled) return;
          if (r.tableId && r.tableTicket) {
            accountTicket.current = r.tableTicket;
            setAccountTableId(r.tableId);
          }
          else if (r.orderId) setPlacedOrderId((held) => held ?? r.orderId);
        })
        .catch(() => {})
        .finally(() => cancelled || setAsking(false));
    ask();
    const onVisible = () => document.visibilityState === "visible" && ask();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [shopOn, tableOrders, signedIn, store.id]);

  // The PowerSync token carries the table and member, so the synced data follows them:
  // reconnect whenever either changes. Sync rules only ever give this phone its own table and member.
  const pickupOrderId = table ? null : placedOrderId; // no table: follow the order itself
  useEffect(() => {
    try {
      // A shop order keeps its id after it is done: the status screen uses it to open the receipt.
      if (pickupOrderId && (isShop || !orderOver)) localStorage.setItem(orderKey(store.id), pickupOrderId);
      else if (!table) localStorage.removeItem(orderKey(store.id));
    } catch {
      // private mode: the order is followed for this visit only
    }
  }, [pickupOrderId, isShop, orderOver, table, store.id]);
  // Once the table's order is over the visit is done: the next reload starts away from the table.
  useEffect(() => writeTable(store.id, orderOver ? null : heldTable, tableTicket), [heldTable, tableTicket, orderOver, store.id]);
  // The account lets go of the table too, so its other devices do not come back to it.
  useEffect(() => {
    if (orderOver && heldTable) void saveTable(store.id, null);
  }, [orderOver, heldTable, store.id]);
  useEffect(() => {
    // The table comes from its ticket (a header every request carries), never from a parameter. The
    // order is followed by id at a table too, so it stays on this phone once it is paid and closed.
    const params: Record<string, string> = {};
    if (placedOrderId) params.order_id = placedOrderId;
    void db.connect(syncConnector({ params, headers: signedIn ? { "x-member-ticket": signedIn.ticket } : {} }));
  }, [table?.id, tableTicket, placedOrderId, signedIn]);
  useEffect(() => () => void db.disconnect(), []);

  const { data: memberRows } = useRows("members", "SELECT * FROM members WHERE id = ?", [memberId ?? ""]);
  const member = memberRows[0] ?? null;

  // The real current-balance row — authoritative for "how many points does this
  // member have right now", instead of replaying crm.point_transactions history.
  const { data: mspRows } = useRows("member_store_points", "SELECT * FROM member_store_points WHERE member_id = ? AND store_id = ?", [
    member?.id ?? "",
    store.id,
  ]);
  const memberStorePoints = hasCrm ? (mspRows[0] ?? null) : null;
  const memberStoreBalance = memberStorePoints?.points ?? 0;
  const memberLifetimePoints = memberStorePoints?.lifetime_points ?? 0;

  const { data: rewardRows } = useRows("rewards", "SELECT * FROM rewards WHERE store_id = ? AND is_active = 1", [store.id]);
  const rewards = hasCrm ? rewardRows : [];

  const { data: voucherRows } = useRows("vouchers", "SELECT * FROM vouchers WHERE member_id = ?", [member?.id ?? ""]);
  const vouchers = hasCrm ? voucherRows : [];

  // Tracked by id once known, so a staff "Move Table" (a PATCH of this row's table_id) keeps it.
  const { data: tableSessionRows } = useRows(
    "table_sessions",
    "SELECT * FROM table_sessions WHERE id = ? OR (? IS NULL AND table_id = ?)",
    [tableSessionId ?? "", tableSessionId, table?.id ?? ""],
  );
  const tableSession =
    (tableSessionId ? tableSessionRows.find((s) => s.id === tableSessionId) : undefined) ??
    tableSessionRows.find((s) => s.status !== "closed") ??
    null;
  const billRequested = tableSession?.status === "bill_requested" || tableSession?.status === "closed";
  const staffCalled = !!tableSession?.staff_called_at;

  // Follows a staff-initiated "Move Table" (POS's TablesScreen.tsx):
  // when the tracked session's table_id no longer matches this guest's own
  // `table` state, re-resolve the real Table row from the store's already-
  // synced tablesCollection (used elsewhere in guest-portal — no new shape
  // needed) and adopt it. Every guest-facing label (Call Staff/Request
  // Bill/Order Status/menu header) derives from `table`, so they all follow
  // automatically once this fires — no per-screen changes needed, and no
  // rescan required.
  const { data: storeTables } = useRows("tables", "SELECT * FROM tables WHERE store_id = ?", [store.id]);
  // The order says where it sits now; the new table needs its own ticket, which the server gives to a
  // guest who holds one in this store and follows that order.
  const movedTo = table && followed[0]?.status === "open" && followed[0].table_id && followed[0].table_id !== table.id ? followed[0].table_id : null;
  useEffect(() => {
    if (!movedTo || !tableTicket || !placedOrderId) return;
    let cancelled = false;
    api<{ table: unknown; ticket: string }>("/auth/guest-table", { ticket: tableTicket, orderId: placedOrderId })
      .then((r) => {
        if (cancelled) return;
        setTicket(r.ticket);
        setTable(tableSchema.parse(r.table));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movedTo]);

  // Scanning a table that already has an open bill (started by POS, or by
  // an earlier scan — this phone's own or someone else's at the same
  // table) must reconnect to that existing order instead of pretending
  // nothing's been ordered yet, or Order Status/Request Bill/Call Staff
  // would all show a blank slate for a table that already has a live
  // order. The order itself is looked up via sales.orders (table_id +
  // status "open") — the same source of truth POS's own TablesScreen uses
  // to decide a table is "occupied" (see openOrderFor/statusOf there) —
  // NOT floor.table_sessions: POS only ever creates that row lazily, the
  // first time someone taps Request Bill or Call Staff from its side
  // (ensureSession), so a table can very much have a live order with no
  // table_sessions row at all yet. That table is still worth adopting
  // here if one exists (so an already-requested bill/called-staff state
  // carries over), just not required. Both reads go straight to the
  // server rather than the local synced tables, which sync
  // every row on the whole platform unfiltered — no guarantee this
  // table's rows have replicated down to this device yet.
  const adoptTable = useCallback(async (t: Table) => {
      try {
        const [orderRes, sessionRes] = await Promise.all([
          fetch(syncApiUrl(`/sales/orders?table_id=${t.id}&status=open&order_by=created_at&order=desc&limit=5`), { headers: apiHeaders() }),
          fetch(syncApiUrl(`/floor/table-sessions?table_id=${t.id}&order_by=created_at&order=desc&limit=20`), { headers: apiHeaders() }),
        ]);
        const orders = orderRes.ok ? ((await orderRes.json()) as { rows: Order[] }).rows : [];
        const sessionRows = sessionRes.ok ? ((await sessionRes.json()) as { rows: TableSession[] }).rows : [];
        const openOrder = orders[0] ?? null;
        const openSession = sessionRows.find((ts) => ts.status !== "closed") ?? null;
        setPlacedOrderId(openOrder?.id ?? openSession?.order_id ?? null);
        setTableSessionId(openSession?.id ?? null);
      } catch {
        setTableSessionId(null);
        setPlacedOrderId(null);
      }
  }, []);
  // With table orders off, a table's QR still opens the store, just without the table.
  const markScanned = useCallback((scannedTable: Table | null, ticket?: string | null) => {
    setTable(scannedTable);
    setTicket(scannedTable ? (ticket ?? null) : null);
    setScanned(true);
    if (!scannedTable) {
      setTableSessionId(null);
      setPlacedOrderId(null);
    } else void saveTable(store.id, scannedTable.id); // the account's other devices join this table
  }, [store.id]);
  // At a table (just scanned, kept from before a reload, or moved by the staff): find its order and session.
  useEffect(() => {
    if (table) void adoptTable(table);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.id]);
  // The account's open order is at a table (it was placed on another device): this device joins
  // that table, so the order and its status are the same on both. No location check here: the
  // account already passed it where it scanned.
  useEffect(() => {
    if (table || !accountTableId || !tableOrders) return;
    const t = storeTables.find((x) => x.id === accountTableId);
    if (t) markScanned(t, accountTicket.current);
    else if (storeTables.length) setAccountTableId(null); // the tables are here and this one is not: stop waiting
  }, [accountTableId, table, tableOrders, storeTables, markScanned]);
  // Until the business's modules are read, whether this store takes table orders is not known either.
  const joining = modulesLoading || asking || (!!accountTableId && !table && tableOrders);

  const addToCart = useCallback((line: Omit<CartLine, "qty" | "key">) => {
    setCart((prev) => {
      const idx = prev.findIndex((c) => c.key === line.productId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx]!, qty: next[idx]!.qty + 1 };
        return next;
      }
      return [...prev, { ...line, key: line.productId, qty: 1 }];
    });
  }, []);

  // Two differently-customized lines of the same product get distinct keys
  // (productId + a sorted signature of the chosen option ids/labels) so
  // they don't merge into one line — matches POS's addConfiguredProduct.
  const addConfiguredItem = useCallback(
    (line: Omit<CartLine, "qty" | "key">, qty: number, selections: CartLineSelection[]) => {
      const signature = selections
        .map((s) => s.optionId ?? s.label)
        .sort()
        .join(",");
      const key = signature ? `${line.productId}|${signature}` : line.productId;
      setCart((prev) => {
        const idx = prev.findIndex((c) => c.key === key);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx]!, qty: next[idx]!.qty + qty };
          return next;
        }
        const variantLabel = selections.length ? selections.map((s) => s.label).join(", ") : null;
        return [...prev, { ...line, key, qty, selections, variantLabel }];
      });
    },
    [],
  );

  const addVariantItem = useCallback((line: Omit<CartLine, "qty" | "key">, qty: number) => {
    const key = `${line.productId}|${line.variantId}`;
    setCart((prev) => (prev.some((c) => c.key === key) ? prev.map((c) => (c.key === key ? { ...c, qty: c.qty + qty } : c)) : [...prev, { ...line, key, qty }]));
  }, []);

  const incQty = useCallback((key: string) => {
    setCart((prev) => prev.map((c) => (c.key === key ? { ...c, qty: c.qty + 1 } : c)));
  }, []);

  const decQty = useCallback((key: string) => {
    setCart((prev) =>
      prev
        .map((c) => (c.key === key ? { ...c, qty: c.qty - 1 } : c))
        .filter((c) => c.qty > 0),
    );
  }, []);

  const qtyOf = useCallback(
    (productId: string) => cart.filter((c) => c.productId === productId).reduce((s, c) => s + c.qty, 0),
    [cart],
  );

  const signInAs = useCallback(
    (id: string, ticket: string) => {
      setSignedIn({ memberId: id, ticket });
      try {
        localStorage.setItem(memberKey(store.business_id), JSON.stringify({ memberId: id, ticket }));
      } catch {
        // ignore
      }
    },
    [store.business_id],
  );

  const signOut = useCallback(() => {
    setSignedIn(null);
    try {
      localStorage.removeItem(memberKey(store.business_id));
    } catch {
      // ignore
    }
  }, [store.business_id]);

  const cartCount = cart.reduce((s, c) => s + c.qty, 0);
  const cartTotal = cart.reduce((s, c) => s + c.qty * c.unitPrice, 0);

  // A signed-in guest's bag is kept with their account, so it is the same on a phone and a desktop.
  // It is read when the store opens and whenever the page comes back into view, and saved a moment
  // after each change. Nothing is saved before the first read, or an empty new page would wipe it.
  // Each save names the version it started from. When another device saved first, this one takes that
  // bag, adds back its own lines that are missing from it, and saves again.
  // ponytail: a line removed on one device at that same moment can come back; merge per line if it bites.
  const bagSeen = useRef<string | null>(null);
  const bagAt = useRef<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const pull = () =>
      void loadBag<CartLine>(store.id).then((bag) => {
        if (cancelled || !bag) return;
        const seen = JSON.stringify([bag.lines, bag.note]);
        if (seen === bagSeen.current) return void (bagAt.current = bag.at);
        bagSeen.current = seen;
        bagAt.current = bag.at;
        setCart(bag.lines);
        setSpecialReqs(bag.note);
      });
    pull();
    const onVisible = () => document.visibilityState === "visible" && pull();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [store.id, signedIn]);
  useEffect(() => {
    const now = JSON.stringify([cart, specialReqs]);
    if (bagSeen.current === null || now === bagSeen.current) return;
    const t = setTimeout(() => {
      bagSeen.current = now;
      void saveBag<CartLine>(store.id, cart, specialReqs, bagAt.current).then((saved) => {
        if (!saved) return;
        bagAt.current = saved.at;
        if (!saved.conflict) return;
        const theirs = saved.conflict.lines;
        bagSeen.current = JSON.stringify([theirs, saved.conflict.note]);
        // Their lines, with this device's own version of a line both have, then the lines only this device has.
        setCart([...theirs.map((t) => cart.find((mine) => mine.key === t.key) ?? t), ...cart.filter((mine) => !theirs.some((t) => t.key === mine.key))]);
        setSpecialReqs(specialReqs || saved.conflict.note);
      });
    }, 600);
    return () => clearTimeout(t);
  }, [cart, specialReqs, store.id]);

  const placeOrder = useCallback(async (opts?: PlaceOptions) => {
    if (!cart.length || placing) return false;
    setPlacing(true);
    setPlaceError(null);
    try {
      const taxRate = Number(store.tax_rate ?? 0);
      const subtotal = cartTotal;
      const discount = Math.min(subtotal, opts?.voucher?.discount ?? opts?.promo?.discount ?? 0);
      const tax = Math.round((subtotal - discount) * taxRate);
      const now = nowIso();

      // Built only on the first attempt — a retry (after a dropped
      // connection makes the caller call placeOrder again with the same
      // cart) reuses the exact same order/ticket/item ids from
      // placeAttemptRef so sync-api's /commit dedup check recognizes it
      // as the same batch instead of creating a real duplicate order.
      if (!placeAttemptRef.current) {
        // Same table keeps ordering onto the SAME open bill/tab until it's
        // paid — this is never a new order/bill for a repeat round, only
        // ever the guest's very first order this session gets one. Whether
        // the kitchen needs a fresh ticket or can fold this round into an
        // already-active one is a separate decision, made below, and never
        // reopens/resets a ticket the kitchen already marked ready/done (see
        // PosApp.tsx's sendToKitchen for the identical rule and why —
        // mixing already-served items with brand-new unprepared ones on one
        // reappearing ticket confused kitchen staff).
        const isNewOrder = !placedOrderId || !!opts?.shop;
        const orderId = isNewOrder ? newId() : placedOrderId!;

        // This order may have been placed by POS or a different guest's
        // phone (see markScanned's existing-order lookup) — ordersCollection/
        // kitchenTicketsCollection sync every row on the whole platform
        // unfiltered, so there's no guarantee this specific row has
        // replicated down to this device yet; reading straight from the
        // server avoids deciding off a stale (or missing) local copy.
        let ticketId: string | null;
        let newTicket: KitchenTicket | null = null;
        if (opts?.shop) {
          ticketId = null; // nothing is cooked: a shop order never reaches a kitchen screen
        } else if (isNewOrder) {
          newTicket = newKitchenTicket(orderId, store.id, now);
          ticketId = newTicket.id;
        } else {
          const ticketRes = await fetch(
            syncApiUrl(`/sales/kitchen-tickets?order_id=${orderId}&order_by=created_at&order=desc&limit=1`),
            { headers: { authorization: `Bearer ${getAuthToken() ?? ""}` } },
          );
          const ticketRows = ticketRes.ok ? ((await ticketRes.json()) as { rows: KitchenTicket[] }).rows : [];
          const latestTicket = ticketRows[0] ?? null;
          const needsNewTicket = !latestTicket || latestTicket.status === "ready" || latestTicket.status === "done";
          if (needsNewTicket) {
            newTicket = newKitchenTicket(orderId, store.id, now);
            ticketId = newTicket.id;
          } else {
            ticketId = latestTicket!.id;
          }
        }

        const items: CommitOrderItemInput[] = cart.map((line) => {
          const itemId = newId();
          return {
            row: {
              id: itemId,
              order_id: orderId,
              product_id: line.productId,
              variant_id: line.variantId ?? null,
              product_name: line.name,
              variant_label: line.variantLabel ?? null,
              unit_price: line.unitPrice,
              // Always overwritten server-side at commit time (sync-api
              // computes the real cost from current catalog data) — this
              // client never knows cost, but the field is required by
              // OrderItem's inferred type (z.default() only makes it
              // optional on input, not output).
              unit_cost: 0,
              quantity: line.qty,
              line_total: line.unitPrice * line.qty,
              is_voided: false,
              notes: null,
              kitchen_ticket_id: ticketId,
              created_at: now,
              updated_at: now,
            },
            selections: (line.selections ?? []).map((sel) => ({
              id: newId(),
              order_item_id: itemId,
              option_id: sel.optionId,
              add_on_id: null,
              selection_type: "variant" as const,
              quantity: 1,
              option_label: sel.label,
              price_delta: sel.priceDelta,
              // Same as unit_cost above — always overwritten server-side.
              cost_delta: 0,
              created_at: now,
            })),
          };
        });

        placeAttemptRef.current = { isNewOrder, orderId, newTicket, items };
      }
      const { isNewOrder, orderId, newTicket, items } = placeAttemptRef.current;

      if (isNewOrder) {
        const pointsEarned = member && hasCrm ? Math.floor((subtotal - discount + tax) / 1000) * earnRate : 0;
        await commitOrder({
          order: {
            id: orderId,
            business_id: store.business_id,
            store_id: store.id,
            order_number: "AUTO",
            order_type: table ? "dine_in" : opts?.delivery ? "online" : "pickup",
            delivery: opts?.delivery ?? null,
            // shown to the guest; the server sets the real fee from the store's settings
            delivery_fee: opts?.delivery ? (opts.deliveryFee ?? 0) : 0,
            payment_proof_key: opts?.proofKey ?? null,
            payment_review: opts?.proofKey ? "pending" : null,
            source: "guest_portal",
            table_id: table?.id ?? null,
            customer_name: opts?.delivery?.name ?? member?.name ?? null,
            loyalty_member_id: member?.id ?? null,
            staff_id: null,
            device_id: null,
            subtotal: subtotal,
            discount: discount,
            discount_source: discount > 0 ? (opts?.voucher ? "voucher" : "promo") : "none",
            employee_discount_percent: null,
            tax: tax,
            rounding_adjustment: 0,
            total: subtotal - discount + tax + (opts?.delivery ? (opts.deliveryFee ?? 0) : 0),
            tender_type: "pending",
            payment_method_name: opts?.paymentMethod ?? null,
            payment_status: "pending",
            promo_id: discount > 0 && !opts?.voucher ? (opts?.promo?.id ?? null) : null,
            voucher_id: discount > 0 ? (opts?.voucher?.id ?? null) : null,
            points_earned: pointsEarned,
            points_redeemed: 0,
            status: "open",
            notes: specialReqs || null,
            refund_reason: null,
            refunded_at: null,
            refunded_by_staff_id: null,
            guest_account_id: null,
            completed_at: null,
            created_at: now,
            updated_at: now,
          },
          kitchenTicket: newTicket ?? undefined,
          items,
          // Folded into the same atomic commit transaction server-side —
          // sync-api recomputes `points` itself from the order's real,
          // server-derived total and crm.loyalty_configs' real earn_rate
          // (see routes/sales.ts), so the value sent here is just a
          // display-parity nicety, not trusted. Same field POS already
          // sends for its own chargeNewSale/settleExistingOrder.
          loyaltyEarn: member && hasCrm && pointsEarned > 0 ? { memberId: member.id, points: pointsEarned } : undefined,
          guestAccountToken: accountToken(),
          memberTicket: signedIn?.ticket,
        });

        // Open a real dine-in table session for this order — Request Bill later
        // updates this row's status instead of stamping a marker into notes.
        if (table) {
          if (tableSessionId) {
            // `Call staff` may have created this session before an order
            // existed. Adopt it instead of inserting a second active row
            // (which also violates the one-open-session-per-table index).
            await updateSession(tableSessionId, { order_id: orderId, updated_at: now });
          } else {
            setTableSessionId(await insertSession({ table_id: table.id, store_id: store.id, order_id: orderId, status: "open", seated_at: now }));
          }
        }
      } else {
        // This order may have been placed by POS or a different guest's
        // phone (see markScanned's existing-session lookup) — ordersCollection
        // syncs every order on the whole platform unfiltered, so there's no
        // guarantee this specific row has replicated down to this device
        // yet. Reading it straight from the server instead of the local
        // synced copy avoids computing the new totals off a stale
        // (or missing) current value and clobbering the real ones.
        const currentRes = await fetch(syncApiUrl(`/sales/orders/${orderId}`), { headers: { authorization: `Bearer ${getAuthToken() ?? ""}` } });
        if (!currentRes.ok) throw new Error("Could not load the existing order");
        const current = ((await currentRes.json()) as { row: Order }).row;
        const newSub = (current?.subtotal ?? 0) + subtotal;
        const newTax = (current?.tax ?? 0) + tax;
        await commitOrder({
          orderUpdate: {
            id: orderId,
            changes: {
              subtotal: newSub,
              tax: newTax,
              total: newSub + newTax,
              notes: specialReqs
                ? current?.notes
                  ? current.notes + " | " + specialReqs
                  : specialReqs
                : current?.notes ?? null,
            },
          },
          kitchenTicket: newTicket ?? undefined,
          items,
        });
      }

      setPlacedOrderId(orderId);
      setCart([]);
      setSpecialReqs("");
      return true;
    } catch (err) {
      setPlaceError(err instanceof Error ? err.message : "Could not place your order. Try again.");
      return false;
    } finally {
      setPlacing(false);
    }
  }, [cart, cartTotal, earnRate, hasCrm, member, placedOrderId, placing, specialReqs, store, table]);

  const askRedeem = useCallback(
    (rewardId: string) => {
      const reward = rewards.find((r) => r.id === rewardId);
      if (!reward) return;
      if (reward.max_redemptions != null && reward.redemption_count >= reward.max_redemptions) {
        setRedeemReward(null);
        setRedeemError(`"${reward.name}" is no longer available`);
        return;
      }
      if (memberStoreBalance < reward.points_cost) {
        setRedeemError(
          `You need ${(reward.points_cost - memberStoreBalance).toLocaleString()} more points to redeem "${reward.name}"`,
        );
        setRedeemReward(null);
        return;
      }
      setRedeemError(null);
      setRedeemReward(reward);
    },
    [memberStoreBalance, rewards],
  );

  const cancelRedeem = useCallback(() => setRedeemReward(null), []);

  const confirmRedeem = useCallback(async () => {
    const reward = redeemReward;
    if (!reward || !member) return null;
    // Server re-verifies balance/availability for real (POST
    // /crm/rewards/:id/redeem, an atomic conditional decrement) — this is
    // just a fast-path UX check to avoid a round trip for an obviously
    // stale local balance; it's not what makes the redeem safe.
    if (memberStoreBalance < reward.points_cost) {
      setRedeemError("Not enough points to redeem that reward");
      setRedeemReward(null);
      return null;
    }
    try {
      // Spends the points, creates the voucher, and bumps
      // rewards.redemption_count — all atomically, server-side (see
      // packages/db/src/collections/crm.ts's redeemReward and
      // services/sync-api/src/routes/crm.ts's /:id/redeem for why this
      // replaced four separate, unauthenticated client writes). No local
      // collection write to reflect afterward — member_store_points/
      // point_transactions/rewards/vouchers are all locked against direct
      // client writes now, so the balance/voucher-list views here pick up
      // the change on the next sync round trip instead.
      const { voucher } = await redeemRewardRequest(reward.id, signedIn?.ticket ?? "");
      setRedeemReward(null);
      setRedeemError(null);
      return voucher.id;
    } catch (err) {
      const code = err instanceof Error ? err.message : "";
      const message =
        code === "insufficient_points"
          ? "Not enough points to redeem that reward"
          : code === "reward_exhausted" || code === "reward_not_found"
            ? `"${reward.name}" is no longer available`
            : code === "store_unavailable"
              ? "This store's loyalty program is temporarily unavailable"
              : "Could not redeem that reward. Try again.";
      setRedeemError(message);
      setRedeemReward(null);
      return null;
    }
  }, [member, memberStoreBalance, redeemReward]);

  const requestBill = useCallback(async () => {
    if (!placedOrderId) return;
    const ts = nowIso();
    if (tableSessionId) {
      await updateSession(tableSessionId, { status: "bill_requested", bill_requested_at: ts, updated_at: ts });
    } else if (table) {
      // Edge case: order was placed before a table session existed for it yet
      // (e.g. table assigned after ordering) — open one directly as requested.
      setTableSessionId(
        await insertSession({ table_id: table.id, store_id: store.id, order_id: placedOrderId, status: "bill_requested", bill_requested_at: ts, seated_at: ts }),
      );
    }
    // No table (pickup order): no table session concept to update.
  }, [placedOrderId, store.id, table, tableSessionId]);

  const callStaff = useCallback(async () => {
    if (!table) return; // pickup session — no table to send anyone to
    const ts = nowIso();
    if (tableSessionId) {
      await updateSession(tableSessionId, { staff_called_at: ts, updated_at: ts });
    } else {
      // No session yet — Call Staff doesn't require an order to already be
      // placed (unlike Request Bill), so open one directly.
      setTableSessionId(
        await insertSession({ table_id: table.id, store_id: store.id, order_id: placedOrderId, status: "open", staff_called_at: ts, seated_at: ts }),
      );
    }
  }, [placedOrderId, store.id, table, tableSessionId]);

  const value = useMemo<GuestSessionValue>(
    () => ({
      store,
      hasCrm,
      table,
      scanned,
      joining,
      markScanned,
      cart,
      cartCount,
      cartTotal,
      addToCart,
      addConfiguredItem,
      addVariantItem,
      isShop,
      tableOrders,
      online,
      fulfilment,
      setFulfilment,
      address,
      saveAddress,
      incQty,
      decQty,
      qtyOf,
      specialReqs,
      setSpecialReqs,
      placedOrderId,
      placing,
      placeError,
      placeOrder,
      memberId,
      member,
      memberPending: !!signedIn && !member,
      signInAs,
      signOut,
      rewards,
      vouchers,
      redeemReward,
      askRedeem,
      cancelRedeem,
      confirmRedeem,
      redeemError,
      memberStoreBalance,
      memberLifetimePoints,
      billRequested,
      requestBill,
      staffCalled,
      callStaff,
    }),
    [
      store,
      hasCrm,
      table,
      scanned,
      joining,
      markScanned,
      cart,
      cartCount,
      cartTotal,
      addToCart,
      addConfiguredItem,
      addVariantItem,
      isShop,
      tableOrders,
      online,
      fulfilment,
      address,
      saveAddress,
      incQty,
      decQty,
      qtyOf,
      specialReqs,
      placedOrderId,
      placing,
      placeError,
      placeOrder,
      memberId,
      member,
      signedIn,
      signInAs,
      signOut,
      rewards,
      vouchers,
      redeemReward,
      askRedeem,
      cancelRedeem,
      confirmRedeem,
      redeemError,
      memberStoreBalance,
      memberLifetimePoints,
      billRequested,
      requestBill,
      staffCalled,
      callStaff,
    ],
  );

  return <GuestSessionContext.Provider value={value}>{children}</GuestSessionContext.Provider>;
}

export function useGuestSession(): GuestSessionValue {
  const ctx = useContext(GuestSessionContext);
  if (!ctx) throw new Error("useGuestSession must be used within GuestSessionProvider");
  return ctx;
}

/** The store session when inside `/store/$storeId`, otherwise null (the directory, the global scanner). */
export function useOptionalGuestSession(): GuestSessionValue | null {
  return useContext(GuestSessionContext);
}
